import { type Session } from '@nhost/nhost-js/auth'
import { nhost } from 'src/nhost'
import { trpc } from 'src/trpc'

const storageKey = 'impersonation'

// Hauteur du bandeau, en pixels. Le décalage de la barre latérale et de la page
// ne la lit plus : il suit la hauteur mesurée de la pile des bandeaux
// (TopBanners, #77), qui peut aussi porter une annonce.
export const impersonationBannerHeight = 44

export interface Impersonation {
  // Null quand la session de retour est perdue : on sait qu'on incarne
  // quelqu'un, mais plus comment redevenir soi-même.
  adminSession: Session | null
  userName: string
}

// L'état est gardé dans le navigateur, pas en mémoire : incarner quelqu'un
// recharge la page de fond en comble, donc rien de vivant ne survit à la
// bascule. C'est aussi ce qui rend le cas « retour perdu » possible — voir
// stopImpersonation.
export function getImpersonation(): Impersonation | null {
  let value: string | null = null
  try {
    value = localStorage.getItem(storageKey)
  } catch {
    // Stockage inaccessible : on ne peut rien affirmer.
    return null
  }
  if (!value) return null

  try {
    const parsed = JSON.parse(value) as Impersonation
    // Une entrée sans nom ni session ne dit rien d'exploitable : on la traite
    // comme perdue plutôt que de faire confiance à sa forme.
    if (!parsed?.adminSession) {
      return { adminSession: null, userName: parsed?.userName || '' }
    }
    return parsed
  } catch {
    // 🔑 ON NE SUPPRIME PAS LA CLÉ ICI, et c'est tout l'enjeu.
    //
    // Une version antérieure le faisait « pour nettoyer ». C'était détruire
    // l'unique chemin de retour : la clé disparue, le bandeau disparaissait
    // avec elle, et on restait dans la peau de l'autre SANS bandeau et SANS
    // sortie — précisément le piège que Q4 devait fermer.
    //
    // Une valeur illisible signifie donc « impersonation en cours, retour
    // perdu », et le bandeau propose alors de se déconnecter.
    return { adminSession: null, userName: '' }
  }
}

// Se connecter à la place de quelqu'un REMPLACE la session administrateur :
// elle est mise de côté d'abord, pour qu'on puisse la reprendre à la sortie.
export async function startImpersonation(userId: string, userName: string) {
  const adminSession = nhost.getUserSession()
  if (!adminSession) {
    throw new Error('No session to come back to')
  }

  const { personalAccessToken } = await trpc.superAdmin.impersonate.mutate({
    userId,
  })

  localStorage.setItem(storageKey, JSON.stringify({ adminSession, userName }))
  try {
    await nhost.auth.signInPAT({ personalAccessToken })
  } catch (error) {
    // L'échange a échoué : on efface l'état plutôt que de laisser un bandeau
    // annoncer une impersonation qui n'a jamais eu lieu.
    localStorage.removeItem(storageKey)
    throw error
  }

  // 🔑 Rechargement complet, et non une navigation : le cache Apollo, le store
  // et les souscriptions portent encore les données de l'administrateur. Une
  // navigation les garderait, et on verrait ses propres organisations sous
  // l'identité de quelqu'un d'autre.
  //
  // 🔑 « / » et RIEN D'AUTRE : c'est là que vit la liste des organisations
  // (route index de PrivateRoute). Une version antérieure visait « /orgs », en
  // raisonnant sur le déploiement Netlify d'upstream où le site vitrine occupe
  // « / » — notre nginx, lui, ne sert que la webapp. Or « /orgs » n'est une
  // route NULLE PART : elle tombait dans l'attrape-tout « :slug/* », dont
  // l'index redirige vers « news », et « /orgs/news » finissait par matcher
  // « orgs/:orgId/* » avec orgId = « news ». Incarner quelqu'un menait donc à
  // une 404, sur un uuid invalide.
  window.location.href = '/'
}

// Sortir, y compris quand il n'y a rien où revenir.
export function stopImpersonation() {
  const impersonation = getImpersonation()

  try {
    localStorage.removeItem(storageKey)
  } catch {
    // Le stockage peut être inaccessible ; la suite compte davantage.
  }

  if (impersonation?.adminSession) {
    // Cas normal : on repose la session administrateur mise de côté.
    nhost.sessionStorage.set(impersonation.adminSession)
    window.location.href = '/admin/users'
    return
  }

  // 🔑 Le retour est perdu (stockage vidé, navigation privée, autre machine).
  // Sans ce chemin, le bouton ne ferait RIEN et on resterait enfermé·e dans la
  // peau de l'autre, sans issue (#98, Q4). On déconnecte : il suffit alors de
  // se reconnecter pour redevenir soi-même.
  nhost.sessionStorage.remove()
  window.location.href = '/'
}
